<template>
  <div class="pb-page py-3">
    <header class="pb-toolbar">
      <div class="pb-toolbar__title">
        <span class="pb-toolbar__accent" aria-hidden="true"></span>
        <div>
          <h1>Packing Batches</h1>
          <p>Generate batches and print invoices, labels and pick lists</p>
        </div>
      </div>
      <div class="pb-toolbar__actions">
        <CButton class="pb-btn pb-btn--primary" :disabled="isGenerating" @click="generateBatch">
          {{ isGenerating ? 'Generating…' : 'Generate Batch' }}
        </CButton>
        <CButton class="pb-btn pb-btn--ghost" :disabled="isLoadingOverview" @click="refreshOverview">
          {{ isLoadingOverview ? 'Refreshing…' : 'Refresh' }}
        </CButton>
      </div>
    </header>

    <CAlert v-if="errorMessage" color="danger" show class="pb-alert">{{ errorMessage }}</CAlert>
    <CAlert v-if="successMessage" color="success" show class="pb-alert">{{ successMessage }}</CAlert>

    <section class="pb-kpi-row">
      <div class="pb-kpi pb-kpi--indigo">
        <div class="pb-kpi__label">Batchable Orders</div>
        <div class="pb-kpi__value">{{ batchableOrders }}</div>
      </div>
      <div class="pb-kpi pb-kpi--cyan">
        <div class="pb-kpi__label">Manifest Pending</div>
        <div class="pb-kpi__value">{{ manifestPending }}</div>
      </div>
    </section>

    <section class="pb-list">
      <header class="pb-list__head">
        <h2>Batches</h2>
        <span class="pb-list__range">{{ pageRangeLabel }}</span>
      </header>

      <div class="pb-list__header-row" role="row">
        <div class="pb-cell pb-cell--chevron" aria-hidden="true"></div>
        <div class="pb-cell pb-cell--id">Batch ID</div>
        <div class="pb-cell pb-cell--status">Status</div>
        <div class="pb-cell pb-cell--count">Order Count</div>
        <div class="pb-cell pb-cell--date">Batch Date</div>
      </div>

      <div v-if="isLoadingOverview" class="pb-list__state">
        <div class="pb-skeleton" v-for="n in 5" :key="`sk-${n}`"></div>
      </div>

      <div v-else-if="!batches.length" class="pb-list__state pb-list__empty">
        <div class="pb-empty-glyph" aria-hidden="true">∅</div>
        <p>No packing batches found.</p>
      </div>

      <div v-else>
        <div
          v-for="batch in batches"
          :key="batch.id"
          class="pb-row"
          :class="{ 'is-expanded': expandedBatchId === batch.id }"
        >
          <div
            class="pb-row__summary"
            role="button"
            tabindex="0"
            :aria-expanded="expandedBatchId === batch.id"
            @click="toggleBatch(batch.id)"
            @keydown.enter.prevent="toggleBatch(batch.id)"
            @keydown.space.prevent="toggleBatch(batch.id)"
          >
            <span class="pb-cell pb-cell--chevron">
              <svg
                class="pb-chevron"
                :class="{ 'is-open': expandedBatchId === batch.id }"
                viewBox="0 0 24 24"
                aria-hidden="true"
              >
                <path d="M9 6l6 6-6 6" stroke="currentColor" stroke-width="2" fill="none" stroke-linecap="round" stroke-linejoin="round" />
              </svg>
            </span>
            <span class="pb-cell pb-cell--id">{{ batch.id }}</span>
            <span class="pb-cell pb-cell--status">
              <span :class="['status-pill', statusPillClass(batch.status)]">{{ formatStatus(batch.status) }}</span>
            </span>
            <span class="pb-cell pb-cell--count">{{ batch.count || 0 }}</span>
            <span class="pb-cell pb-cell--date">{{ formatDate(batch.created_at) }}</span>
          </div>

          <div v-if="expandedBatchId === batch.id" class="pb-row__body">
            <div v-if="detailLoading" class="pb-row__loading">Loading batch…</div>
            <div v-else-if="detailError" class="pb-row__error">{{ detailError }}</div>
            <template v-else>
              <div v-if="!detailGroups.length" class="pb-row__empty">No orders in this batch.</div>
              <div v-else class="pb-courier-list">
                <div v-for="(group, gIndex) in detailGroups" :key="gIndex" class="pb-courier-row">
                  <div class="pb-courier-name">
                    <span class="pb-courier-name__text">{{ courierLabel(group) }}</span>
                    <span class="pb-courier-name__count">{{ group.length }} order{{ group.length === 1 ? '' : 's' }}</span>
                  </div>
                  <div class="pb-courier-actions">
                    <CButton class="pb-courier-btn" @click="printInvoices(group)">Print Invoice</CButton>
                    <CButton class="pb-courier-btn" @click="printLabels(group)">Print Label</CButton>
                  </div>
                </div>
              </div>
              <CButton class="pb-picklist-btn" @click="generatePickList(batch.id)">
                Generate Pick List
              </CButton>
            </template>
          </div>
        </div>
      </div>
    </section>

    <div v-if="!isLoadingOverview && batches.length && lastPage > 1" class="pb-pagination-bar">
      <span class="pb-pagination-info">Page {{ currentPage }} of {{ lastPage }}</span>
      <AppPager :current-page="currentPage" :last-page="lastPage" @change="goToPage" />
    </div>
  </div>
</template>

<script>
import axios from 'axios'
import AppPager from '@/components/AppPager.vue'

export default {
  name: 'PackingBatches',
  components: { AppPager },
  data () {
    return {
      batchableOrders: 0,
      manifestPending: 0,
      batches: [],
      currentPage: 1,
      lastPage: 1,
      perPage: 10,
      totalBatches: 0,
      expandedBatchId: null,
      detailGroups: [],
      detailLoading: false,
      detailError: '',
      isLoadingOverview: false,
      isGenerating: false,
      errorMessage: '',
      successMessage: '',
      detailRequestId: 0
    }
  },
  computed: {
    pageRangeLabel () {
      if (!this.totalBatches) return '0 batches'
      const start = (this.currentPage - 1) * this.perPage + 1
      const end = Math.min(this.currentPage * this.perPage, this.totalBatches)
      return `${start}–${end} of ${this.totalBatches}`
    }
  },
  created () {
    this.loadOverview(1)
  },
  methods: {
    token () {
      return localStorage.getItem('api_token')
    },
    apiBase () {
      if (typeof window !== 'undefined') {
        const host = window.location.hostname
        if (host === 'localhost' || host === '127.0.0.1') {
          return ''
        }
      }
      return this.$apiAdress
    },
    formatDate (value) {
      if (!value) return '—'
      const raw = String(value).replace('T', ' ').slice(0, 10)
      const parts = raw.split('-')
      if (parts.length === 3) return `${parts[2]}-${parts[1]}-${parts[0]}`
      return raw
    },
    formatStatus (status) {
      if (!status) return 'N/A'
      return String(status).replace(/_/g, ' ')
    },
    statusPillClass (status) {
      const s = String(status || '').toLowerCase()
      if (s === 'packed' || s === 'completed') return 'badge--green'
      if (s === 'cancelled' || s === 'failed') return 'badge--red'
      if (s === 'packing_pending' || s === 'pending') return 'badge--yellow'
      return 'badge--gray'
    },
    courierLabel (group) {
      const courier = group && group[0] && group[0].courier
      if (!courier) return 'Unassigned Courier'
      return String(courier).replace(/_/g, ' ').toUpperCase()
    },
    parseError (error, fallback) {
      const validationErrors = error?.response?.data?.error
      if (validationErrors && typeof validationErrors === 'object') {
        const key = Object.keys(validationErrors)[0]
        if (key && Array.isArray(validationErrors[key]) && validationErrors[key][0]) {
          return validationErrors[key][0]
        }
      }
      return error?.response?.data?.message || fallback
    },
    refreshOverview () {
      this.loadOverview(this.currentPage)
    },
    goToPage (page) {
      this.expandedBatchId = null
      this.detailGroups = []
      this.detailError = ''
      this.loadOverview(page)
    },
    loadOverview (page = 1) {
      const targetPage = Number(page) > 0 ? Number(page) : 1
      this.errorMessage = ''
      this.successMessage = ''
      this.isLoadingOverview = true

      axios.get(`${this.$apiAdress}/api/packing-batch?page=${targetPage}&token=${this.token()}`)
        .then((response) => {
          const data = response.data || {}
          this.batchableOrders = Number(data.batchable_orders || 0)
          this.manifestPending = Number(data.manifest_pending || 0)

          const paginator = data.packing_batches || {}
          this.batches = Array.isArray(paginator.data) ? paginator.data : []
          this.currentPage = Number(paginator.current_page || 1)
          this.lastPage = Number(paginator.last_page || 1)
          this.perPage = Number(paginator.per_page || 10)
          this.totalBatches = Number(paginator.total || this.batches.length)

          if (this.expandedBatchId && !this.batches.some((b) => b.id === this.expandedBatchId)) {
            this.expandedBatchId = null
            this.detailGroups = []
          }
        })
        .catch((error) => {
          console.log(error)
          this.errorMessage = this.parseError(error, 'Unable to load packing batches.')
        })
        .finally(() => {
          this.isLoadingOverview = false
        })
    },
    toggleBatch (id) {
      if (this.expandedBatchId === id) {
        this.expandedBatchId = null
        this.detailGroups = []
        this.detailError = ''
        return
      }
      this.expandedBatchId = id
      this.loadBatchDetails(id)
    },
    loadBatchDetails (id) {
      const requestId = ++this.detailRequestId
      this.detailLoading = true
      this.detailError = ''
      this.detailGroups = []

      axios.get(`${this.$apiAdress}/api/packing-batch/${id}?token=${this.token()}`)
        .then((response) => {
          if (requestId !== this.detailRequestId) return
          this.detailGroups = Array.isArray(response.data) ? response.data : []
        })
        .catch((error) => {
          if (requestId !== this.detailRequestId) return
          console.log(error)
          this.detailError = this.parseError(error, 'Unable to load batch details.')
        })
        .finally(() => {
          if (requestId !== this.detailRequestId) return
          this.detailLoading = false
        })
    },
    generateBatch () {
      this.errorMessage = ''
      this.successMessage = ''
      this.isGenerating = true

      axios.post(`${this.$apiAdress}/api/packing-batch?token=${this.token()}`, {})
        .then((response) => {
          this.successMessage = response.data?.message || 'Packing batch generated successfully.'
          const newId = response.data?.packing_batch_id || null
          this.expandedBatchId = null
          this.detailGroups = []
          this.loadOverview(1)
          if (newId) {
            this.$nextTick(() => {
              this.expandedBatchId = newId
              this.loadBatchDetails(newId)
            })
          }
        })
        .catch((error) => {
          console.log(error)
          this.errorMessage = this.parseError(error, 'Unable to generate a packing batch.')
        })
        .finally(() => {
          this.isGenerating = false
        })
    },
    openPrintWindow (url) {
      const win = window.open('', '_blank')
      if (!win) {
        this.errorMessage = 'Pop-up blocked. Please allow pop-ups for this site and try again.'
        return
      }
      win.document.write('<!doctype html><title>Loading…</title><body style="font-family:system-ui;padding:2rem;color:#475569">Loading…</body>')
      axios.get(url)
        .then((response) => {
          win.document.open()
          win.document.write(response.data)
          win.document.close()
        })
        .catch((error) => {
          console.log(error)
          const message = this.parseError(error, 'Failed to load print preview.')
          win.document.open()
          win.document.write(`<!doctype html><title>Error</title><body style="font-family:system-ui;padding:2rem;color:#991b1b">${message}</body>`)
          win.document.close()
        })
    },
    printInvoices (group) {
      const ids = (group || []).map((o) => o.id).filter(Boolean)
      if (!ids.length) return
      this.openPrintWindow(`${this.apiBase()}/invoices?orders=${ids.join(',')}`)
    },
    printLabels (group) {
      const orders = Array.isArray(group) ? group : []
      const pickrrIds = orders
        .filter((o) => o.shipping_method === 'pickrr' && o.aggregator_order_id_pk)
        .map((o) => o.id)
      const otherIds = orders
        .filter((o) => !(o.shipping_method === 'pickrr' && o.aggregator_order_id_pk))
        .map((o) => o.id)

      if (otherIds.length) {
        this.openPrintWindow(`${this.apiBase()}/post-office-labels?orders=${otherIds.join(',')}`)
      }
      if (pickrrIds.length) {
        this.openPrintWindow(`${this.apiBase()}/pickrr-labels?orders=${pickrrIds.join(',')}`)
      }
    },
    generatePickList (batchId) {
      if (!batchId) return
      this.openPrintWindow(`${this.apiBase()}/pick-list/${batchId}`)
    }
  }
}
</script>

<style scoped>
.pb-page {
  padding: 0.25rem 0;
}

.pb-toolbar {
  display: flex;
  align-items: center;
  justify-content: space-between;
  flex-wrap: wrap;
  gap: 0.75rem;
  padding: 0.25rem 0.25rem 1rem;
}

.pb-toolbar__title {
  display: flex;
  align-items: center;
  gap: 0.65rem;
}

.pb-toolbar__accent {
  width: 4px;
  height: 36px;
  border-radius: 4px;
  background: linear-gradient(180deg, #0e5b46 0%, #16a07a 100%);
  flex-shrink: 0;
}

.pb-toolbar__title h1 {
  margin: 0;
  font-size: 1.15rem;
  font-weight: 700;
  color: #1f2a44;
  letter-spacing: -0.01em;
  line-height: 1.2;
}

.pb-toolbar__title p {
  margin: 0;
  font-size: 0.78rem;
  color: #6b7280;
}

.pb-toolbar__actions {
  display: flex;
  align-items: center;
  gap: 0.5rem;
  flex-wrap: wrap;
}

.pb-btn {
  height: 36px;
  border-radius: 8px;
  font-weight: 600;
  font-size: 0.82rem;
  padding: 0 1rem;
  transition: transform 0.15s ease, box-shadow 0.15s ease, filter 0.15s ease;
}

.pb-btn--primary {
  background: linear-gradient(135deg, #0e5b46 0%, #16a07a 100%);
  border: 0;
  color: #ffffff;
  box-shadow: 0 2px 6px rgba(14, 91, 70, 0.18);
}

.pb-btn--primary:hover:not(:disabled) {
  transform: translateY(-1px);
  box-shadow: 0 6px 14px rgba(14, 91, 70, 0.28);
  filter: brightness(1.05);
}

.pb-btn--ghost {
  background: #ffffff;
  border: 1px solid #d8dde6;
  color: #1f2a44;
}

.pb-btn--ghost:hover:not(:disabled) {
  border-color: #16a07a;
  color: #0e5b46;
}

.pb-btn:disabled {
  opacity: 0.7;
  cursor: progress;
}

.pb-alert {
  margin: 0 0 0.75rem;
  border-radius: 10px;
}

.pb-kpi-row {
  display: grid;
  grid-template-columns: repeat(2, minmax(0, 1fr));
  gap: 0.85rem;
  margin-bottom: 1rem;
}

.pb-kpi {
  background: #ffffff;
  border: 1px solid #e5e9f2;
  border-left: 4px solid transparent;
  border-radius: 12px;
  padding: 0.85rem 1rem;
  box-shadow: 0 2px 6px rgba(15, 23, 42, 0.04);
}

.pb-kpi__label {
  font-size: 0.72rem;
  font-weight: 700;
  letter-spacing: 0.06em;
  text-transform: uppercase;
  color: #6b778d;
  margin-bottom: 0.25rem;
}

.pb-kpi__value {
  font-size: 1.45rem;
  font-weight: 700;
  color: #18233a;
  line-height: 1.1;
}

.pb-kpi--indigo {
  border-left-color: #3f51b5;
  background: linear-gradient(140deg, #ffffff 60%, #eef1ff 100%);
}

.pb-kpi--cyan {
  border-left-color: #0097a7;
  background: linear-gradient(140deg, #ffffff 60%, #e9fbff 100%);
}

.pb-list {
  background: #ffffff;
  border: 1px solid #e5e9f2;
  border-radius: 12px;
  box-shadow: 0 2px 6px rgba(15, 23, 42, 0.04);
  overflow: hidden;
}

.pb-list__head {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 0.5rem;
  padding: 0.7rem 1rem;
  border-bottom: 1px solid #eef1f7;
  background: #fbfcfe;
}

.pb-list__head h2 {
  margin: 0;
  font-size: 0.95rem;
  font-weight: 700;
  color: #1f2a44;
}

.pb-list__range {
  font-size: 0.78rem;
  color: #6b7280;
}

.pb-list__header-row {
  display: grid;
  grid-template-columns: 48px 1fr 1fr 1fr 1fr;
  align-items: center;
  padding: 0.55rem 0.85rem;
  background: #f6f8fc;
  border-bottom: 1px solid #eef1f7;
  font-size: 0.72rem;
  font-weight: 700;
  text-transform: uppercase;
  letter-spacing: 0.04em;
  color: #6b778d;
}

.pb-cell {
  padding: 0 0.25rem;
  font-size: 0.85rem;
  color: #1f2a44;
}

.pb-cell--chevron {
  display: flex;
  align-items: center;
  justify-content: center;
  color: #6b7280;
}

.pb-list__state {
  padding: 1rem;
}

.pb-skeleton {
  height: 44px;
  border-radius: 8px;
  background: linear-gradient(90deg, #f1f5f9 0%, #e2e8f0 50%, #f1f5f9 100%);
  background-size: 200% 100%;
  animation: pb-skeleton 1.2s ease-in-out infinite;
  margin-bottom: 0.5rem;
}

.pb-skeleton:last-child {
  margin-bottom: 0;
}

@keyframes pb-skeleton {
  0% { background-position: 200% 0; }
  100% { background-position: -200% 0; }
}

.pb-list__empty {
  text-align: center;
  color: #6b7280;
  padding: 2rem 1rem;
}

.pb-empty-glyph {
  font-size: 1.75rem;
  color: #cbd5e1;
  margin-bottom: 0.25rem;
}

.pb-list__empty p {
  margin: 0;
  font-size: 0.85rem;
}

.pb-row {
  border-bottom: 1px solid #eef1f7;
  transition: background-color 0.15s ease;
}

.pb-row:last-child {
  border-bottom: 0;
}

.pb-row.is-expanded {
  background: #f6faf8;
}

.pb-row__summary {
  display: grid;
  grid-template-columns: 48px 1fr 1fr 1fr 1fr;
  align-items: center;
  padding: 0.7rem 0.85rem;
  cursor: pointer;
  user-select: none;
  outline: none;
}

.pb-row__summary:hover {
  background: rgba(22, 160, 122, 0.05);
}

.pb-row__summary:focus-visible {
  background: rgba(22, 160, 122, 0.08);
  box-shadow: inset 0 0 0 2px rgba(22, 160, 122, 0.3);
}

.pb-chevron {
  width: 16px;
  height: 16px;
  transition: transform 0.2s ease;
}

.pb-chevron.is-open {
  transform: rotate(90deg);
  color: #0e5b46;
}

.pb-row__body {
  padding: 0.5rem 0.85rem 1rem;
  background: #f6faf8;
  border-top: 1px dashed #d6e4dd;
}

.pb-row__loading,
.pb-row__error,
.pb-row__empty {
  padding: 1rem;
  text-align: center;
  font-size: 0.85rem;
  color: #6b7280;
}

.pb-row__error {
  color: #991b1b;
}

.pb-courier-list {
  display: flex;
  flex-direction: column;
  gap: 0.5rem;
  margin-bottom: 0.85rem;
}

.pb-courier-row {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 0.75rem;
  flex-wrap: wrap;
  background: #ffffff;
  border: 1px solid #e5e9f2;
  border-radius: 10px;
  padding: 0.6rem 0.85rem;
}

.pb-courier-name {
  display: flex;
  align-items: baseline;
  gap: 0.5rem;
  min-width: 0;
}

.pb-courier-name__text {
  font-size: 0.85rem;
  font-weight: 700;
  color: #1f2a44;
  letter-spacing: 0.02em;
}

.pb-courier-name__count {
  font-size: 0.72rem;
  color: #6b7280;
}

.pb-courier-actions {
  display: flex;
  gap: 0.5rem;
  flex-wrap: wrap;
}

.pb-courier-btn {
  height: 32px;
  padding: 0 0.85rem;
  border-radius: 7px;
  border: 1px solid #16a07a;
  background: #ffffff;
  color: #0e5b46;
  font-size: 0.78rem;
  font-weight: 600;
  transition: background 0.15s ease, color 0.15s ease, transform 0.15s ease;
}

.pb-courier-btn:hover:not(:disabled) {
  background: linear-gradient(135deg, #0e5b46 0%, #16a07a 100%);
  color: #ffffff;
  border-color: transparent;
  transform: translateY(-1px);
}

.pb-picklist-btn {
  width: 100%;
  height: 38px;
  border-radius: 8px;
  background: linear-gradient(135deg, #0e5b46 0%, #16a07a 100%);
  border: 0;
  color: #ffffff;
  font-weight: 600;
  font-size: 0.85rem;
  box-shadow: 0 2px 6px rgba(14, 91, 70, 0.18);
  transition: transform 0.15s ease, box-shadow 0.15s ease, filter 0.15s ease;
}

.pb-picklist-btn:hover:not(:disabled) {
  transform: translateY(-1px);
  box-shadow: 0 6px 14px rgba(14, 91, 70, 0.28);
  filter: brightness(1.05);
}

.pb-pagination-bar {
  display: flex;
  align-items: center;
  justify-content: space-between;
  margin: 0.75rem 0 1rem;
  flex-wrap: wrap;
  gap: 0.5rem;
}

.pb-pagination-info {
  font-size: 0.78rem;
  color: #6b7280;
}

@media (max-width: 768px) {
  .pb-kpi-row { grid-template-columns: minmax(0, 1fr); }
  .pb-list__header-row,
  .pb-row__summary {
    grid-template-columns: 36px 1fr 1fr 0.7fr 1fr;
    font-size: 0.78rem;
  }
}

.status-pill {
  display: inline-flex;
  align-items: center;
  padding: 0.12rem 0.55rem;
  border-radius: 999px;
  font-size: 0.67rem;
  font-weight: 700;
  white-space: nowrap;
  text-transform: capitalize;
  letter-spacing: 0.02em;
}

.badge--green  { background: #dff5ea; color: #0e5b46; }
.badge--yellow { background: #fef3c7; color: #92400e; }
.badge--red    { background: #fee2e2; color: #991b1b; }
.badge--gray   { background: #f1f5f9; color: #475569; }
</style>
